import React from "react";

/**
 * RowErrorBoundary — isolates a single list item so that a bad row
 * (e.g. missing field the parent didn't guard against) doesn't tear
 * down the whole grid.
 *
 * Usage:
 *   <RowErrorBoundary label="Philosophy · France">
 *     <CountryColumn row={row} ... />
 *   </RowErrorBoundary>
 *
 * Design intent:
 *  - Keep the visual footprint of a crashed row so the grid layout
 *    doesn't shift underneath the user.
 *  - Log to console (and PostHog if available) so we get real-world
 *    coverage on tiers/subjects our test data never hit.
 *  - Never swallow errors silently in dev — throwing errors are how
 *    we find the next `SOURCE_TIER_LABELS.verified` bug.
 */
export default class RowErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Unknown error" };
  }

  componentDidCatch(error, info) {
    // Ship to PostHog if the user consented; otherwise stay on the
    // console. Wrapped in try/catch because posthog is a browser stub
    // during SSR and may not be initialised on the first render tick.
    try {
      if (typeof window !== "undefined" && window.posthog?.captureException) {
        window.posthog.captureException(error, {
          extra: {
            row_label: this.props.label || "unlabelled",
            component_stack: info?.componentStack?.slice(0, 400),
          },
        });
      }
    } catch { /* posthog stub may not be ready */ }
    // Always log to console so devs see it in preview.
    // eslint-disable-next-line no-console
    console.error(
      `[RowErrorBoundary] "${this.props.label || "row"}" crashed:`,
      error,
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Custom fallback wins if the caller supplied one; otherwise a
    // neutral card that keeps grid alignment.
    if (typeof this.props.fallback === "function") {
      return this.props.fallback(this.state.message);
    }
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        data-testid={`row-error-boundary-fallback${this.props.label ? `-${this.props.label}` : ""}`}
        className="p-5 rounded-md border border-rose-200 bg-rose-50 text-sm text-rose-800"
      >
        <div className="font-semibold">Couldn't display this row.</div>
        <div className="mt-1 text-xs text-rose-700/80">
          Our team has been notified. Refresh to try again, or move on to the next row.
        </div>
      </div>
    );
  }
}
